import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Menu, 
  X, 
  Globe, 
  User, 
  ChevronDown,
  Home,
  Compass,
  Shield,
  Sparkles,
  LogOut,
  Sun,
  Moon,
  Ticket,
  Heart,
  LayoutDashboard,
  LogIn,
  UserPlus
} from 'lucide-react';

interface HeaderProps {
  user?: {
    name?: string;
    firstName?: string;
    avatar?: string;
    email?: string;
    role?: string;
  };
  onLogout?: () => void;
  showNavigation?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  user: userProp,
  onLogout,
  showNavigation = true,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout, isAuthenticated } = useAuth();
  const { openLogin, openSignup } = useAuthModal();
  const { theme, toggleTheme } = useTheme();
  
  // Use authUser from context if no user prop is provided
  const user = userProp || authUser;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const navigationItems = [
    { 
      key: 'home', 
      label: t('navigation.home', 'Home'), 
      href: '/', 
      icon: Home,
      description: 'Discover events'
    },
    { 
      key: 'about', 
      label: t('navigation.about', 'About'), 
      href: '/about', 
      icon: Compass,
      description: 'Learn about our platform'
    }
  ];

  const userMenuItems: Array<{
    key: string;
    label: string;
    href: string;
    icon: React.ComponentType<any>;
    description: string;
  }> = [];

  // Add role-based menu items (use authUser for accurate role data)
  if (authUser?.role === 'master' || authUser?.isMaster) {
    userMenuItems.push({
      key: 'master',
      label: t('header.masterDashboard', 'Master Dashboard'),
      href: '/master-dashboard',
      icon: Shield,
      description: t('header.masterDashboardDesc', 'Platform management')
    });
  } else if (authUser?.role === 'admin') {
    userMenuItems.push({
      key: 'admin',
      label: t('header.adminPanel', 'Admin Dashboard'),
      href: '/admin-dashboard',
      icon: Shield,
      description: t('header.adminPanelDesc', 'Event management')
    });
  } else if (authUser?.role === 'user') {
    // Regular user menu items
    userMenuItems.push(
      {
        key: 'dashboard',
        label: t('navigation.dashboard', 'My Dashboard'),
        href: '/dashboard',
        icon: LayoutDashboard,
        description: t('navigation.dashboardDesc', 'Your account overview')
      },
      {
        key: 'tickets',
        label: t('navigation.tickets', 'My Tickets'),
        href: '/tickets',
        icon: Ticket,
        description: t('navigation.ticketsDesc', 'View your tickets')
      },
      {
        key: 'favorites',
        label: t('navigation.favorites', 'My Favorites'),
        href: '/favorites',
        icon: Heart,
        description: t('navigation.favoritesDesc', 'Saved events')
      }
    );
  }

  const isCurrentPath = (path: string) => location.pathname === path;

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsMenuOpen(false);
  };

  const handleUserAction = (action: string) => {
    setIsUserMenuOpen(false);
    if (action === 'logout') {
      logout();
      navigate('/');
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 overflow-visible ${
        isScrolled 
          ? 'liquid-glass-header backdrop-blur-xl border-b border-white/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-visible">
        <div className="flex justify-between items-center h-16 overflow-visible">
          {/* Enhanced Logo */}
          <div 
            className="flex items-center space-x-3 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <div className="relative">
              <div className="w-10 h-10 liquid-glass-strong rounded-2xl flex items-center justify-center liquid-glow group-hover:scale-110 transition-transform">
                <span className="text-white font-bold text-lg">
                  S
                </span>
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-white to-gray-300 rounded-full animate-pulse"></div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-white font-bold text-lg tracking-tight group-hover:text-white/90 transition-colors">
                ShufeShow
              </h1>
              <p className="text-white/60 text-xs">Jordan & Beyond</p>
            </div>
          </div>

          {/* Enhanced Navigation - Desktop */}
          {showNavigation && (
            <nav className="hidden lg:flex items-center space-x-2">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = isCurrentPath(item.href);
                return (
                  <button
                    key={item.key}
                    onClick={() => handleNavigation(item.href)}
                    className={`group relative px-4 py-2 rounded-2xl transition-all duration-300 hover:scale-105 ${
                      isActive 
                        ? 'liquid-glass-strong text-white liquid-glow' 
                        : 'liquid-glass-button text-white/80 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <IconComponent size={18} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    
                    {/* Tooltip */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-black/80 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                      {item.description}
                    </div>
                  </button>
                );
              })}
            </nav>
          )}

          {/* Enhanced Action Buttons */}
          <div className="flex items-center space-x-3">

            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              className="w-10 h-10 liquid-glass-button rounded-2xl flex items-center justify-center text-white liquid-glow hover:scale-110 transition-all duration-300 group"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-black/80 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </div>
            </button>

            {/* Language Toggle */}
            <button 
              onClick={toggleLanguage}
              className="w-10 h-10 liquid-glass-button rounded-2xl flex items-center justify-center text-white liquid-glow hover:scale-110 transition-all duration-300 group"
              title={i18n.language === 'en' ? 'العربية' : 'English'}
            >
              <Globe size={18} />
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-black/80 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </div>
            </button>

            {/* Login/Signup buttons for unauthenticated users */}
            {!isAuthenticated && (
              <div className="hidden sm:flex items-center space-x-2">
                <button
                  onClick={openLogin}
                  className="flex items-center space-x-2 px-4 py-2 liquid-glass-button rounded-2xl text-white hover:scale-105 transition-all duration-300 text-sm font-medium"
                  aria-label={t('auth.login', 'Sign In')}
                >
                  <LogIn size={16} />
                  <span>{t('auth.login', 'Sign In')}</span>
                </button>
                <button
                  onClick={openSignup}
                  className="flex items-center space-x-2 px-4 py-2 liquid-glass-strong rounded-2xl text-white hover:scale-105 transition-all duration-300 text-sm font-medium liquid-glow"
                  aria-label={t('auth.signup', 'Sign Up')}
                >
                  <UserPlus size={16} />
                  <span>{t('auth.signup', 'Sign Up')}</span>
                </button>
              </div>
            )}

            {/* User Menu */}
            {user && (
              <div className="relative z-50">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsUserMenuOpen(!isUserMenuOpen);
                  }}
                  className="flex items-center space-x-3 liquid-glass-button rounded-2xl px-4 py-2 text-white hover:scale-105 transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-2">
                    {user.avatar ? (
                                              <img
                          src={user.avatar}
                          alt={user.firstName}
                          className="w-8 h-8 rounded-full border-2 border-white/30 object-cover"
                        />
                    ) : (
                      <div className="w-8 h-8 liquid-glass-strong rounded-full flex items-center justify-center">
                        <User size={16} />
                      </div>
                    )}
                    <span className="font-medium hidden sm:block">
                      {user.firstName}
                    </span>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={`transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute top-full right-0 mt-3 w-64 liquid-glass-card rounded-2xl shadow-2xl overflow-hidden border border-white/10 z-[100] transform translate-y-0">
                    {/* User Info */}
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center space-x-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.firstName}
                            className="w-12 h-12 rounded-full border-2 border-white/30 object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 liquid-glass-strong rounded-full flex items-center justify-center">
                            <User size={20} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const dashboardPath = authUser?.role === 'master' || authUser?.isMaster ? '/master' : authUser?.role === 'admin' ? '/admin' : '/dashboard';
                              navigate(dashboardPath);
                              setIsUserMenuOpen(false);
                            }}
                            className="block text-white font-semibold hover:text-white/80 transition-colors cursor-pointer text-left w-full py-1 hover:bg-white/5 rounded px-1 -mx-1"
                          >
                            {user.firstName} {(user as any).lastName || ''}
                          </button>
                          <div className="text-white/60 text-sm mt-1">{user.email}</div>
                          {user.role && (
                            <div className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full text-xs text-white/80 mt-1">
                              <Sparkles size={12} className="mr-1" />
                              {user.role}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {userMenuItems.map((item) => {
                        const IconComponent = item.icon;
                        return (
                          <button
                            key={item.key}
                            onClick={() => {
                              handleNavigation(item.href);
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center space-x-3 text-white/90 hover:text-white"
                          >
                            <IconComponent size={18} />
                            <div>
                              <div className="font-medium">{item.label}</div>
                              <div className="text-xs text-white/60">{item.description}</div>
                            </div>
                          </button>
                        );
                      })}
                      
                      <div className="border-t border-white/10 mt-2 pt-2">
                        <button
                          onClick={() => handleUserAction('logout')}
                          className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center space-x-3 text-red-400 hover:text-red-300"
                        >
                          <LogOut size={18} />
                          <div>
                            <div className="font-medium">{t('header.logout', 'Sign Out')}</div>
                            <div className="text-xs text-white/60">{t('header.signOutDescription', 'Sign out of your account')}</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="lg:hidden w-10 h-10 liquid-glass-button rounded-2xl flex items-center justify-center text-white liquid-glow hover:scale-110 transition-all duration-300"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-2 sm:mx-4 liquid-glass-card rounded-2xl shadow-2xl overflow-hidden border border-white/10 z-50 max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="p-3 sm:p-4">
              {/* Mobile Navigation */}
              {showNavigation && (
                <div className="space-y-2 mb-4">
                  {navigationItems.map((item) => {
                    const IconComponent = item.icon;
                    const isActive = isCurrentPath(item.href);
                    return (
                      <button
                        key={item.key}
                        onClick={() => {
                          handleNavigation(item.href);
                          setIsMenuOpen(false);
                        }}
                        className={`w-full px-4 py-3 min-h-[48px] rounded-2xl transition-all duration-300 flex items-center space-x-3 touch-manipulation ${
                          isActive 
                            ? 'liquid-glass-strong text-white liquid-glow' 
                            : 'liquid-glass-button text-white/80 hover:text-white active:scale-95'
                        }`}
                      >
                        <IconComponent size={20} className="flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-medium text-sm sm:text-base">{item.label}</div>
                          <div className="text-xs text-white/60 hidden sm:block">{item.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Mobile Login/Signup for unauthenticated users */}
              {!isAuthenticated && (
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <button
                    onClick={() => {
                      openLogin();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 min-h-[48px] rounded-2xl liquid-glass-button text-white/80 hover:text-white transition-all duration-300 flex items-center space-x-3 touch-manipulation active:scale-95"
                  >
                    <LogIn size={20} className="flex-shrink-0" />
                    <div className="text-left flex-1">
                      <div className="font-medium text-sm sm:text-base">{t('auth.login', 'Sign In')}</div>
                      <div className="text-xs text-white/60">{t('auth.loginDesc', 'Access your account')}</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      openSignup();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 min-h-[48px] rounded-2xl liquid-glass-strong text-white hover:text-white transition-all duration-300 flex items-center space-x-3 touch-manipulation active:scale-95 liquid-glow"
                  >
                    <UserPlus size={20} className="flex-shrink-0" />
                    <div className="text-left flex-1">
                      <div className="font-medium text-sm sm:text-base">{t('auth.signup', 'Sign Up')}</div>
                      <div className="text-xs text-white/60">{t('auth.signupDesc', 'Create a new account')}</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Mobile User Menu */}
              {user && (
                <div className="border-t border-white/10 pt-4 space-y-2">
                  {userMenuItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => {
                          handleNavigation(item.href);
                          setIsMenuOpen(false);
                        }}
                        className="w-full px-4 py-3 min-h-[48px] rounded-2xl liquid-glass-button text-white/80 hover:text-white transition-all duration-300 flex items-center space-x-3 touch-manipulation active:scale-95"
                      >
                        <IconComponent size={20} className="flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-medium text-sm sm:text-base">{item.label}</div>
                          <div className="text-xs text-white/60 hidden sm:block">{item.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
